import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgModel,
    UntypedFormControl,
    ValidationErrors,
    Validator,
} from '@angular/forms';

import { CommonMessages, DeviceService } from '@frontend/vanilla/core';
import { DateConfig, FormFieldComponent } from '@frontend/vanilla/shared/forms';

import { NumberAllowedOnlyDirective } from '../directives/number.directive';
import { CommonMethodsService } from '../tracking/common-methods-service';

/**
 * @stable
 */
export class DateModel {
    day: any | null;
    month: any | null;
    year: any | null;
}

/**
 * @whatItDoes Provides day month year picker component and returns javascript Date object
 *
 * @stable
 */
@Component({
    selector: 'pt-date',
    templateUrl: 'pt-date.component.html',
    providers: [
        { provide: NG_VALIDATORS, useExisting: DateComponent, multi: true },
        { provide: NG_VALUE_ACCESSOR, useExisting: DateComponent, multi: true },
    ],
    imports: [FormFieldComponent, NgClass, FormsModule, NgTemplateOutlet, NumberAllowedOnlyDirective],
})
export class DateComponent implements OnInit, ControlValueAccessor, Validator {
    /** Specifies if component is required */
    @Input() required: boolean;
    @Input() newLayout: boolean;
    /** Specifies callback when date changes */
    @Output() onDateChanged: EventEmitter<Date | null> = new EventEmitter();
    /** Specifies callback when day changes */
    @Output() onDayChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies callback when month changes */
    @Output() onMonthChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies callback when year changes */
    @Output() onYearChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies if autocomplete is enabled */
    @Input() autocomplete: boolean;
    /** context of the registration */
    @Input() areaName: boolean;
    @Input() subAreaName: boolean;

    /** Specifies if scroll is need for conversation registration */
    @Output() focusFieldEventCompleted: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('day') dayElement: NgModel;
    @ViewChild('month') monthElement: NgModel;
    @ViewChild('year') yearElement: NgModel;

    isDisabled: boolean;
    dayPlaceholder: string;
    monthPlaceholder: string;
    yearPlaceholder: string;
    model: DateModel = { day: null, month: null, year: null };
    days: number[] = [];
    daysInMonth: any;
    ready: boolean;
    showMonthFirst: boolean = false;
    private touchedElements = new Set<any>();
    private control: UntypedFormControl;

    constructor(
        private commonMessages: CommonMessages,
        private elementRef: ElementRef,
        private deviceService: DeviceService,
        public dateConfig: DateConfig,
        private commonService: CommonMethodsService,
    ) {}

    onChange: (value: any) => void = () => {};
    onTouched = () => {};

    async ngOnInit() {
        if (!this.dateConfig.isConfigReady) await this.commonService.loadClientConfig([DateConfig]);
        this.dayPlaceholder = this.commonMessages['DateDayPlaceholder'];
        this.monthPlaceholder = this.commonMessages['DateMonthPlaceholder'];
        this.yearPlaceholder = this.commonMessages['DateYearPlaceholder'];
        this.showMonthFirst = this.dateConfig.showMonthFirst;
    }

    validate(c: UntypedFormControl): ValidationErrors | null {
        this.control = c;
        const date = new Date();
        // set default amount of days relying on first month of year
        const defAmountOfDays = new Date(date.getFullYear(), 0).getDate();
        const year = this.model.year ? parseInt(this.model.year) : date.getFullYear();
        const month = this.model.month ? this.model.month : defAmountOfDays;

        this.daysInMonth = new Date(year, month, 0).getDate();

        //Default to 31 days for day value range if month is invalid.
        if (this.model.month && (this.model.month < 1 || this.model.month > 12)) {
            this.daysInMonth = 31;
        }
        this.days = this.range(1, this.daysInMonth);

        if (this.control.errors && (this.control.errors.olderthan || this.control.errors.youngerthan)) {
            this.updateValidity();
            return null;
        }

        if (this.model.day && (isNaN(this.model.day) || this.days.indexOf(parseInt(this.model.day)) == -1)) {
            this.dayElement.control.setErrors({ required: true });
            this.yearElement.control.setErrors({ required: true });
            if (this.days.length === 31) {
                return { invaliddatepart: true };
            } else if (this.days.length === 30) {
                return { invaliddatepart30days: true };
            } else if (this.days.length === 29) {
                return { invaliddatepart29days: true };
            } else if (this.days.length === 28) {
                return { invaliddatepart28days: true };
            }
        } else if (this.dayElement && this.days.indexOf(parseInt(this.model.day)) > -1) {
            this.dayElement.control.setErrors(null);
        }

        if (this.dayElement && this.dayElement.hasError('required')) {
            this.yearElement.control.setErrors({ required: true });
        }

        if (this.model.month && (this.model.month < 1 || this.model.month > 12)) {
            this.monthElement.control.setErrors({ required: true });
            this.yearElement.control.setErrors({ required: true });
            return { invalidmonthpart: true };
        } else if (this.monthElement && this.monthElement.hasError('required')) {
            this.yearElement.control.setErrors({ required: true });
        }

        if (this.model.year && this.model.year.length < 4) {
            this.yearElement.control.setErrors({ required: true });
            return { invalidyearpart: true };
        }
        return null;
    }

    writeValue(obj: Date | string): void {
        if (obj) {
            if (typeof obj === 'string') {
                obj = new Date(obj);
            }

            this.model.year = obj.getFullYear().toString();
            this.model.month = obj.getMonth() + 1;
            this.model.day = obj.getDate();
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    range(start: number, end: number): number[] {
        const result = [];
        for (let i = start; i <= end; i++) {
            result.push(i);
        }
        return result;
    }

    getDate(): Date | null {
        if (this.model.year && this.model.month && this.model.day) {
            //Immediately triggers validation when date component is pre-filled and user does not touch all the inputs.
            this.onTouched();
            const date = new Date(parseInt(this.model.year), this.model.month - 1, this.model.day, 12, 0, 0, 0);
            //Confirm that the values returned from the generated date are the same as passed, since new Date() never throws errors for dates like 31/02.
            if (date.getFullYear().toString() == this.model.year && date.getMonth() == this.model.month - 1 && date.getDate() == this.model.day) {
                return date;
            }
        }
        return null;
    }

    onModelChange(event: number, element: string) {
        this.model = { ...this.model, [element]: event };
        this.dayElement.control.updateValueAndValidity();
        this.control.updateValueAndValidity();
        const selectedDate = this.getDate();
        this.onChange(selectedDate);

        if (this.onDateChanged) {
            this.onDateChanged.emit(selectedDate);
        }

        if (element === 'day' || element === 'month') {
            this.moveToNext(element);
        }

        this.updateValidity();
    }

    triggerTouched(element: any) {
        if (!this.touchedElements.has(element)) {
            this.touchedElements.add(element);
        }

        if (this.touchedElements.size === 3) {
            this.onTouched();
        }
    }

    onDayChange(day: Event) {
        const element = this.elementRef.nativeElement.querySelector('input[name="day"]');
        if (element.value.length === 1) {
            element.value = '0' + element.value;
        }
        this.triggerTouched(day.target);
        this.onDayChanged.emit(day);
    }

    onMonthChange(month: Event) {
        const element = this.elementRef.nativeElement.querySelector('input[name="month"]');
        if (element.value.length === 1) {
            element.value = '0' + element.value;
        }
        this.triggerTouched(month.target);
        this.onMonthChanged.emit(month);
    }

    onYearChange(year: Event) {
        this.triggerTouched(year.target);
        this.onYearChanged.emit(year);
    }

    moveToNext(element: string) {
        const currentElement = this.elementRef.nativeElement.querySelector('input[name="' + element + '"]');
        const nextElement = this.getNextElement(element);
        if (currentElement.value.length >= currentElement.maxLength) {
            this.elementRef.nativeElement.querySelector('input[name="' + nextElement + '"]').focus();
        }
    }

    getNextElement(element: string) {
        if (element === 'day') {
            return this.dateConfig.showMonthFirst ? 'year' : 'month';
        } else if (element === 'month') {
            return this.dateConfig.showMonthFirst ? 'day' : 'year';
        }
        return;
    }
    onFieldFocus() {
        if (this.areaName && this.subAreaName && this.deviceService.isMobilePhone) {
            this.focusFieldEventCompleted.emit(true);
        }
    }
    private updateValidity() {
        if (this.control.errors) {
            if (this.control.errors.youngerthan || this.control.errors.olderthan) {
                this.dayElement.control.setErrors({ required: true });
                this.dayElement.control.markAsTouched();

                this.monthElement.control.setErrors({ required: true });
                this.monthElement.control.markAsTouched();

                this.yearElement.control.setErrors({ required: true });
                this.yearElement.control.markAsTouched();
            }
        } else {
            this.dayElement.control.setErrors(null);
            this.monthElement.control.setErrors(null);
            this.yearElement.control.setErrors(null);
        }
    }
}
